import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
} from "@mui/material";
import { BiChevronDown } from "react-icons/bi";
import {
  BudgetItemType,
  BudgetItemTypeEnum,
  CategoryTotals,
  HeaderOptions,
  initCategoryTotals,
  TotalByType,
  TotalSplited,
} from "../../../../types/budget/budget";
import {
  createOptionsFromEnum,
  formatCurrency,
} from "../../../../utils/formatters";
import { ServiceData } from "../../../../types/budget/service-item";
import { useMemo } from "react";
import BaseValuesTotal from "../BaseValuesTotal";
import { currencyFormatter } from "../../../../utils/currencyFormatter";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";

type Props = {
  type: HeaderOptions;
  totalPerCalcOption: TotalByType;
  services: ServiceData[];
};

export default function TotalAccordion({
  services,
  totalPerCalcOption,
  type,
}: Props) {
  const { versionInfo } = useBudget();
  const itemTypeOptions = createOptionsFromEnum(BudgetItemTypeEnum);
  const currentCurrencies = useMemo(() => {
    return services.map((service) => {
      if (versionInfo.status === "APPROVED") {
        return service.closingValues?.exchangeData?.currency || "BRL";
      }

      if (versionInfo.version > 0) {
        return (
          service.negotiationValue?.[versionInfo.version - 1]?.exchangeData
            ?.currency || "BRL"
        );
      }

      return service.budgetValues.exchangeData?.currency || "BRL";
    });
  }, [services, versionInfo]);

  const displayCurrencyCode = useMemo(() => {
    const uniqueCurrencies = Array.from(new Set(currentCurrencies));

    if (uniqueCurrencies.length === 1 && uniqueCurrencies[0] !== "BRL") {
      return uniqueCurrencies[0];
    }

    return undefined;
  }, [currentCurrencies]);

  const showIofConverted = useMemo(() => {
    return currentCurrencies.some((currency) => currency !== "BRL");
  }, [currentCurrencies]);

  function renderBudgetItemSummary() {
    return itemTypeOptions.map(
      (el: { value: BudgetItemType; text: string }) => {
        const servicesPerType = services.filter(
          (service) => service.type === el.value
        );
        const total = reduceTotalsArr(totalPerCalcOption[el.value]);

        if (servicesPerType.length < 1) return <></>;
        return (
          <BudgetItemSummary
            type={el.value}
            servicesLength={servicesPerType.length}
            totals={total}
          />
        );
      }
    );
  }

  function reduceTotalsArr(arr: TotalSplited[]): CategoryTotals {
    return (
      arr?.reduce(
        (acc: CategoryTotals, item: TotalSplited) => {
          acc.totals.totalInBRL += item.totals.totalInBRL;
          acc.totals.totalInCurrency += item.totals.totalInCurrency;
          acc.revenueTotals.totalInBRL += item.revenueTotals.totalInBRL;
          acc.revenueTotals.totalInCurrency +=
            item.revenueTotals.totalInCurrency;
          acc.serviceTaxTotals.totalInBRL += item.serviceTaxTotals.totalInBRL;
          acc.serviceTaxTotals.totalInCurrency +=
            item.serviceTaxTotals.totalInCurrency;
          acc.tariffTotals.totalInBRL += item.tariffTotals.totalInBRL;
          acc.tariffTotals.totalInCurrency += item.tariffTotals.totalInCurrency;
          acc.issTotals.totalInBRL += item.issTotals.totalInBRL;
          acc.issTotals.totalInCurrency += item.issTotals.totalInCurrency;
          acc.cbsTotals.totalInBRL += item.cbsTotals?.totalInBRL ?? 0;
          acc.cbsTotals.totalInCurrency += item.cbsTotals?.totalInCurrency ?? 0;
          acc.ibsTotals.totalInBRL += item.ibsTotals?.totalInBRL ?? 0;
          acc.ibsTotals.totalInCurrency += item.ibsTotals?.totalInCurrency ?? 0;
          acc.localTaxTotals.totalInBRL += item.localTaxTotals.totalInBRL;
          acc.localTaxTotals.totalInCurrency +=
            item.localTaxTotals.totalInCurrency;
          acc.tourismTaxTotals.totalInBRL += item.tourismTaxTotals.totalInBRL;
          acc.tourismTaxTotals.totalInCurrency +=
            item.tourismTaxTotals.totalInCurrency;
          acc.feeTotals.totalInBRL += item.feeTotals.totalInBRL;
          acc.feeTotals.totalInCurrency += item.feeTotals.totalInCurrency;
          acc.markupTotals.totalInBRL += item.markupTotals.totalInBRL;
          acc.markupTotals.totalInCurrency += item.markupTotals.totalInCurrency;
          acc.overheadTotals.totalInBRL += item.overheadTotals.totalInBRL;
          acc.overheadTotals.totalInCurrency +=
            item.overheadTotals.totalInCurrency;
          acc.commissionTotals.totalInBRL += item.commissionTotals.totalInBRL;
          acc.commissionTotals.totalInCurrency +=
            item.commissionTotals.totalInCurrency;
          acc.incentiveTotals.totalInBRL += item.incentiveTotals.totalInBRL;
          acc.incentiveTotals.totalInCurrency +=
            item.incentiveTotals.totalInCurrency;
          acc.iofTotals.totalInBRL += item.iofTotals.totalInBRL;
          acc.iofTotals.totalInCurrency += item.iofTotals.totalInCurrency;
          acc.tributeTotals.totalInBRL += item.tributeTotals.totalInBRL;
          acc.tributeTotals.totalInCurrency +=
            item.tributeTotals.totalInCurrency;
          acc.taxes.totalInBRL += item.taxes.totalInBRL;
          acc.taxes.totalInCurrency += item.taxes.totalInCurrency;

          return acc;
        },
        JSON.parse(JSON.stringify(initCategoryTotals)) // Initial accumulator value
      ) || JSON.parse(JSON.stringify(initCategoryTotals))
    );
  }
  return (
    <Accordion sx={{ mb: 1 }} TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary
        sx={{
          background: "#F8F8F8",
          borderRadius: "4px",
        }}
        expandIcon={<BiChevronDown />}
      >
        <span style={{ fontWeight: "bold" }}>{type}</span>
        <Box ml={"auto"} mr={1}>
          <span>
            {formatCurrency(totalPerCalcOption.SUM.totals.totalInBRL)}
          </span>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {renderBudgetItemSummary()}
        <Divider sx={{ m: 1 }} />
        <BaseValuesTotal
          totals={totalPerCalcOption.SUM}
          showExtras={false}
          displayCurrencyCode={displayCurrencyCode}
          showIofConverted={showIofConverted}
        />
      </AccordionDetails>
    </Accordion>
  );
}

type BudgetItemSummaryProps = {
  type: BudgetItemType;
  servicesLength: number;
  totals: CategoryTotals;
};
function BudgetItemSummary({
  type,
  servicesLength,
  totals,
}: BudgetItemSummaryProps) {
  const totalsSummary = useMemo(() => {
    return [
      { title: "T. Tarifas", value: totals.tariffTotals },
      { title: "T. Taxas", value: totals.serviceTaxTotals },
      { title: "T. Receita", value: totals.revenueTotals },
      { title: "T. Imposto", value: totals.taxes },
      { title: "T. Geral", value: totals.totals },
    ];
  }, [totals]);
  return (
    <Box
      display={"flex"}
      width={"100%"}
      justifyContent={"space-between"}
      gap={4}
    >
      <Box ml={3} flex={0.2}>
        <Box>{BudgetItemTypeEnum[type]}</Box>
        <Box color={"#aaa"}>{servicesLength} cadastros</Box>
      </Box>
      <Box display="flex" flex={0.8} justifyContent={"space-evenly"} gap={1}>
        {totalsSummary.map((i) => (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
            }}
            flex={1}
          >
            <span
              style={{
                color: "rgba(1,1,1,0.5)",
                fontSize: 14,
              }}
            >
              {i.title}
            </span>
            <span style={{ fontSize: 14 }}>
              {currencyFormatter.format(i.value.totalInBRL)}
            </span>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
